import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Workflow } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAutomationWorkspaceContext } from '../contexts/AutomationWorkspaceContext';
import { useTabActivity } from '../contexts/TabActivityContext';
import { commitOwnedSearch } from '../navigation/historyState';
import { getAutomationRuntimeBadge } from '../utils/automationRuntime';
import IndexAutomationPanel from './AutomationsTab';
import type { PromptRun } from '../services/api';

interface IndexAutomationControlProps {
  indexId: string;
  runs: PromptRun[];
  onSelectIndex: (indexId: string) => void;
  disabled?: boolean;
  disabledReason?: string;
}

const IndexAutomationControl: React.FC<IndexAutomationControlProps> = ({ indexId, runs, onSelectIndex, disabled = false, disabledReason }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isActive: isTabActive, portalContainer } = useTabActivity();
  const workspace = useAutomationWorkspaceContext();
  const loaded = workspace.selectedIndex?.index_id === indexId
    && workspace.hasLoadedImport && workspace.hasLoadedExport;
  const importStatus = loaded && workspace.savedImportDraft
    ? getAutomationRuntimeBadge(workspace.savedImportDraft) : null;
  const exportStatus = loaded && workspace.savedExportDraft
    ? getAutomationRuntimeBadge(workspace.savedExportDraft) : null;
  const isActive = importStatus?.className === 'active' || exportStatus?.className === 'active';
  const requestedView = new URLSearchParams(location.search).get('automation');
  const isOpen = requestedView === 'import' || requestedView === 'export';
  const close = useCallback(() => {
    if (!isTabActive || workspace.isSavingImport || workspace.isSavingExport) return;
    commitOwnedSearch({ location, navigate, ownedKeys: ['automation'], updates: { automation: null }, mode: 'replace' });
  }, [isTabActive, location, navigate, workspace.isSavingImport, workspace.isSavingExport]);

  return (
    <div className="index-automation-control index-disabled-action" title={disabledReason}>
      <button type="button" className={`index-automation-button${isActive ? ' is-active' : ''}`}
        onClick={() => commitOwnedSearch({
          location, navigate, ownedKeys: ['automation'],
          updates: { automation: importStatus?.className === 'active' ? 'import' : exportStatus?.className === 'active' ? 'export' : 'import' },
          mode: 'push',
        })}
        disabled={disabled} aria-haspopup="dialog" aria-expanded={!disabled && isOpen}
        title={disabledReason || "Configure this index's import and export automations"}>
        <Workflow size={16} />
        Automation
      </button>
      {importStatus && workspace.savedImportDraft?.enabled && (
        <span className={`automations-status automations-status--${importStatus.className}`}>Import · {importStatus.label}</span>
      )}
      {exportStatus && workspace.savedExportDraft?.enabled && (
        <span className={`automations-status automations-status--${exportStatus.className}`}>Export · {exportStatus.label}</span>
      )}
      {!disabled && isOpen && createPortal(
        <IndexAutomationPanel onClose={close} onSelectIndex={onSelectIndex} runs={runs} />,
        portalContainer || document.body,
      )}
    </div>
  );
};

export default IndexAutomationControl;
